<script setup lang="ts">
import { RouterLink } from 'vue-router'
import { useSeo } from '@/composables/useSeo'
import { useAppStore } from '@/stores/app'
import { registry, categories } from '@/calculators/registry'
import type { AnyCalculatorDefinition } from '@/types/calculator'
import Alert from '@/components/ui/Alert.vue'
import Icon from '@/components/icons/Icon.vue'
import HeroSection from '@/components/home/HeroSection.vue'
import CalculatorCard from '@/components/home/CalculatorCard.vue'
import CategoryCard from '@/components/home/CategoryCard.vue'

const app = useAppStore()
const siteUrl = app.siteUrl.replace(/\/$/, '')

useSeo({
  title: 'Free South African Financial Calculators',
  description:
    'Free South African calculators for salary, PAYE, VAT, fuel, bonds, solar, cars and rental yield — fast, transparent estimates with sources shown.',
  path: '/',
  keywords: [
    'South Africa calculator',
    'PAYE calculator',
    'VAT calculator',
    'bond calculator',
    'solar calculator',
  ],
  jsonLd: [
    {
      '@type': 'WebSite',
      name: app.siteName,
      url: siteUrl,
      potentialAction: {
        '@type': 'SearchAction',
        target: `${siteUrl}/calculators?q={search_term_string}`,
        'query-input': 'required name=search_term_string',
      },
    },
    {
      '@type': 'Organization',
      name: app.siteName,
      url: siteUrl,
    },
  ],
})

const popularSlugs = [
  'salary-calculator',
  'vat-calculator',
  'bond-calculator',
  'fuel-cost-calculator',
]

const popular = popularSlugs
  .map((slug) => registry.find((c) => c.slug === slug))
  .filter((c): c is AnyCalculatorDefinition => Boolean(c))

const categoryMap = Object.fromEntries(categories.map((c) => [c.id, c]))
</script>

<template>
  <main class="home">
    <HeroSection />

    <section class="section section--highlight">
      <div class="section__grid" aria-hidden="true"></div>
      <div class="container section__inner">
        <div class="section__header">
          <div class="section__heading">
            <span class="section__eyebrow">Most used</span>
            <h2 class="section__title">Popular calculators</h2>
          </div>
          <RouterLink to="/calculators" class="section__link">
            View all <Icon name="arrow-right" :size="16" />
          </RouterLink>
        </div>
        <div class="grid grid--popular">
          <CalculatorCard
            v-for="calc in popular"
            :key="calc.id"
            :calc="calc"
            :category-label="categoryMap[calc.category]?.label"
            :category-icon="categoryMap[calc.category]?.icon"
          />
        </div>
      </div>
    </section>

    <section class="container section">
      <div class="section__heading">
        <span class="section__eyebrow">Explore</span>
        <h2 class="section__title">Browse by category</h2>
      </div>
      <div class="grid grid--categories">
        <CategoryCard
          v-for="category in categories"
          :id="category.id"
          :key="category.id"
          :label="category.label"
          :icon="category.icon"
        />
      </div>
    </section>

    <section class="container section">
      <Alert title="Transparent and independent" variant="info">
        <p>
          We show our formulas, assumptions and source data for every calculator. Results are
          estimates, not advice — see our <RouterLink to="/disclaimer">disclaimer</RouterLink>.
        </p>
      </Alert>
    </section>
  </main>
</template>

<style scoped>
.section {
  position: relative;
  padding: var(--space-10) 0;
}

.section--highlight {
  background:
    radial-gradient(circle at 90% 10%, rgb(217 119 6 / 0.05), transparent 35%),
    radial-gradient(circle at 10% 90%, rgb(10 92 86 / 0.05), transparent 35%), var(--color-bg);
  border-top: 1px solid var(--color-border);
  border-bottom: 1px solid var(--color-border);
  overflow: hidden;
}

.section__grid {
  position: absolute;
  inset: 0;
  opacity: 0.35;
  background-image: radial-gradient(var(--color-border-strong) 1px, transparent 1px);
  background-size: 28px 28px;
  mask-image: linear-gradient(to bottom, black 20%, transparent 80%);
  -webkit-mask-image: linear-gradient(to bottom, black 20%, transparent 80%);
}

.section__inner {
  position: relative;
}

.section__header {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: var(--space-4);
  margin-bottom: var(--space-5);
}

.section__heading {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
}

.section__eyebrow {
  font-size: var(--text-xs);
  font-weight: var(--font-semibold);
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--color-primary);
}

.section__title {
  font-size: var(--text-2xl);
  font-weight: var(--font-bold);
  margin: 0;
}

.section__link {
  display: inline-flex;
  align-items: center;
  gap: var(--space-1);
  color: var(--color-primary);
  font-weight: var(--font-medium);
  font-size: var(--text-sm);
}

.grid {
  display: grid;
  gap: var(--space-4);
}

.grid--popular {
  grid-template-columns: 1fr;
}

@media (min-width: 640px) {
  .grid--popular {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (min-width: 1024px) {
  .grid--popular {
    grid-template-columns: repeat(4, 1fr);
  }
}

.grid--categories {
  grid-template-columns: repeat(2, 1fr);
}

@media (min-width: 640px) {
  .grid--categories {
    grid-template-columns: repeat(3, 1fr);
  }
}

@media (min-width: 1024px) {
  .grid--categories {
    grid-template-columns: repeat(5, 1fr);
  }
}
</style>
